import { z } from 'zod'

// --- Client-only form types (not in the API spec) ---

export interface LicenseFormVisitType {
  visitTypeId: string
  serviceName: string
  visitName: string
  isSelected: boolean
  visibilityOverride: string
}

export type LicenseEntryType = 'Medical' | 'CSL' | 'DEA'

export interface LicenseEntry {
  type: LicenseEntryType
  number: string
  deaStateCode: string
}

export interface LicenseFormValues {
  stateCode: string
  licenseEntries: LicenseEntry[]
  visitTypes: LicenseFormVisitType[]
}

export interface ServiceType {
  ServiceTypeId: string
  Name: string
  VisitTypes: { VisitTypeId: string; Name: string; VisibilityType?: string | null }[]
}

const LICENSE_NUMBER_PATTERN = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9.\- ]+$/
const visitTypeSchema = z.object({
  visitTypeId: z.string(),
  serviceName: z.string(),
  visitName: z.string(),
  isSelected: z.boolean(),
  visibilityOverride: z.string(),
})

const licenseEntrySchema = z.object({
  type: z.enum(['Medical', 'CSL', 'DEA']),
  number: z.string(),
  deaStateCode: z.string(),
})

export const licenseFormSchema = z
  .object({
    stateCode: z.string().min(1, 'State is required'),
    licenseEntries: z.array(licenseEntrySchema).min(1),
    visitTypes: z
      .array(visitTypeSchema)
      .refine((arr) => arr.some((v) => v.isSelected), 'At least one visit type is required'),
  })
  .superRefine((data, ctx) => {
    data.licenseEntries.forEach((entry, idx) => {
      if (!entry.number.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'License number is required',
          path: ['licenseEntries', idx, 'number'],
        })
      } else if (!LICENSE_NUMBER_PATTERN.test(entry.number)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Invalid license number format',
          path: ['licenseEntries', idx, 'number'],
        })
      }

      if (entry.type === 'DEA') {
        if (!entry.deaStateCode) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'State is required for DEA licenses',
            path: ['licenseEntries', idx, 'deaStateCode'],
          })
        }
      }
    })
  })

export type LicenseFormSchema = z.infer<typeof licenseFormSchema>
